import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import { uploadUrl } from '../utils/uploads';
import { JOB_STATUS_LABELS } from '../utils/statusConstants';

const JobDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completionNotes, setCompletionNotes] = useState('');
  const [timeSpent, setTimeSpent] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/jobs/${id}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const startJob = async () => {
    setActionLoading(true);
    try {
      await apiFetch(`/jobs/${id}/start`, { method: 'POST' });
      toast.success('Job started');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const uploadPhoto = async (phase, file) => {
    const fd = new FormData();
    fd.append('phase', phase);
    fd.append('file', file);
    try {
      await apiFetch(`/jobs/${id}/photos`, { method: 'POST', body: fd });
      toast.success(`${phase} photo uploaded`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const completeJob = async () => {
    setActionLoading(true);
    try {
      await apiFetch(`/jobs/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          completion_notes: completionNotes || null,
          time_spent_minutes: timeSpent ? Number(timeSpent) : null,
        }),
      });
      toast.success('Job marked complete');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <p className="text-slate-500">Loading job…</p>;
  if (!data) return null;

  const { job, photos } = data;
  const beforePhotos = photos.filter((p) => p.phase === 'before');
  const afterPhotos = photos.filter((p) => p.phase === 'after');
  const canStart = ['assigned', 'confirmed'].includes(job.status);
  const inProgress = job.status === 'in_progress';
  const completed = job.status === 'completed';

  return (
    <div>
      <Link to="/jobs" className="text-sm text-teal-700 hover:underline">
        ← Back to jobs
      </Link>
      <h1 className="text-2xl font-semibold text-teal-800 mt-4">{job.job_number}</h1>
      <p className="text-slate-600 mt-1">
        {JOB_STATUS_LABELS[job.status] || job.status}
        {job.scheduled_start && (
          <span> · {new Date(job.scheduled_start).toLocaleString()}</span>
        )}
      </p>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6 space-y-2 text-sm">
        <p>
          <span className="text-slate-500">Location:</span>{' '}
          {job.location_label || job.address_line1}, {job.city}, {job.state} {job.postal_code}
        </p>
        {job.access_notes && (
          <p>
            <span className="text-slate-500">Access:</span> {job.access_notes}
          </p>
        )}
        {job.notes && (
          <p>
            <span className="text-slate-500">Notes:</span> {job.notes}
          </p>
        )}
      </div>

      {canStart && (
        <button
          type="button"
          disabled={actionLoading}
          onClick={startJob}
          className="mt-6 bg-teal-700 text-white px-6 py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50"
        >
          Start job
        </button>
      )}

      {(inProgress || completed) && (
        <div className="mt-8 space-y-8">
          <section>
            <h2 className="font-semibold text-slate-800 mb-3">Before photos</h2>
            <div className="flex flex-wrap gap-3 mb-3">
              {beforePhotos.map((p) => (
                <a key={p.id} href={uploadUrl(p.url)} target="_blank" rel="noreferrer">
                  <img src={uploadUrl(p.url)} alt="Before" className="h-24 w-24 object-cover rounded-lg border" />
                </a>
              ))}
            </div>
            {inProgress && (
              <label className="inline-flex px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm cursor-pointer">
                Upload before photo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && uploadPhoto('before', e.target.files[0])}
                />
              </label>
            )}
          </section>

          <section>
            <h2 className="font-semibold text-slate-800 mb-3">After photos</h2>
            <div className="flex flex-wrap gap-3 mb-3">
              {afterPhotos.map((p) => (
                <a key={p.id} href={uploadUrl(p.url)} target="_blank" rel="noreferrer">
                  <img src={uploadUrl(p.url)} alt="After" className="h-24 w-24 object-cover rounded-lg border" />
                </a>
              ))}
            </div>
            {inProgress && (
              <label className="inline-flex px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm cursor-pointer">
                Upload after photo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && uploadPhoto('after', e.target.files[0])}
                />
              </label>
            )}
          </section>

          {inProgress && (
            <section className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-800 mb-4">Complete job</h2>
              <label className="block text-sm font-medium mb-1">Time spent (minutes)</label>
              <input
                type="number"
                min="1"
                max="1440"
                className="w-full max-w-xs border rounded-lg px-3 py-2 mb-4"
                value={timeSpent}
                onChange={(e) => setTimeSpent(e.target.value)}
              />
              <label className="block text-sm font-medium mb-1">Completion notes</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 mb-4 min-h-[100px]"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="What was done, any issues, etc."
              />
              <button
                type="button"
                disabled={actionLoading}
                onClick={completeJob}
                className="bg-teal-700 text-white px-6 py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50"
              >
                Mark complete
              </button>
              <p className="text-xs text-slate-500 mt-2">
                Requires at least one before and one after photo.
              </p>
            </section>
          )}

          {completed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-900 text-sm">
              Completed
              {job.time_spent_minutes != null && ` · ${job.time_spent_minutes} min`}
              {job.completion_notes && (
                <p className="mt-2 text-green-800">{job.completion_notes}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobDetail;
